/**
 * The sequencer's scheduling arithmetic. Notes are never played "now": a timer wakes every
 * 25 ms or so, and every step that falls inside the next `LOOKAHEAD` seconds is handed to
 * WebAudio with its exact start time on the audio clock. Timers jitter; the audio clock does
 * not. This file is the part with no WebAudio in it, so it can be tested with a fake clock.
 */

import { STEPS_PER_BEAT } from './pattern';

/** Seconds of audio scheduled ahead of the clock. */
export const LOOKAHEAD = 0.12;
/** How often the timer fires, ms. Several wake-ups per look-ahead window, so one late timer is harmless. */
export const TICK_MS = 25;
/** A step this far in the past was missed (a throttled background tab): skip it, do not play it late. */
const LATE = 0.03;

export interface SeqClock {
  /** Audio-clock time of step 0. */
  startTime: number;
  stepSeconds: number;
  /** The next absolute step (it keeps counting through loops) not yet handed to WebAudio. */
  nextStep: number;
}

export const stepSecondsFor = (bpm: number): number => 60 / bpm / STEPS_PER_BEAT;

export function createClock(startTime: number, bpm: number): SeqClock {
  return { startTime, stepSeconds: stepSecondsFor(bpm), nextStep: 0 };
}

export const stepTime = (c: SeqClock, step: number): number => c.startTime + step * c.stepSeconds;

/**
 * Move `nextStep` past anything already too late to play, and return how many steps were
 * dropped. The grid itself never moves, so the music stays in time with itself afterwards.
 */
export function skipMissed(c: SeqClock, now: number): number {
  const first = Math.ceil((now - LATE - c.startTime) / c.stepSeconds);
  if (first <= c.nextStep) return 0;
  const dropped = first - c.nextStep;
  c.nextStep = first;
  return dropped;
}

/**
 * The steps that start before `now + lookAhead` and have not been scheduled yet, appended to
 * `out` (absolute step numbers); advances the clock past them. Returns how many were added.
 */
export function collectDue(c: SeqClock, now: number, lookAhead: number, out: number[]): number {
  let added = 0;
  while (stepTime(c, c.nextStep) < now + lookAhead) {
    out.push(c.nextStep);
    c.nextStep++;
    added++;
  }
  return added;
}
